import { Helmet } from "react-helmet-async";
import { Star } from "lucide-react";
import { testimonials } from "@/content/testimonials";
import { GOOGLE_MAPS_URL, GOOGLE_REVIEW_URL } from "@/content/reviewLinks";
import { gtagReportConversion } from "@/lib/gtag";

interface TestimonialsProps {
  /** Optional category filter — show only reviews matching */
  category?: "glamping" | "cabanas" | "tubing" | "family" | "service";
  /** Section heading override */
  heading?: string;
  /** Subtitle override */
  subtitle?: string;
  /** Max number of reviews to display */
  limit?: number;
}

export function Testimonials({
  category,
  heading = "Real reviews from real guests",
  subtitle = "Verified 5-star reviews from Google.",
  limit,
}: TestimonialsProps) {
  const filtered = category
    ? testimonials.filter((t) => t.category === category)
    : testimonials;
  const items = limit ? filtered.slice(0, limit) : filtered;

  if (items.length === 0) return null;

  // Review schema — emitted via Helmet so it merges into <head>
  const reviewSchemas = items.map((t) => ({
    "@context": "https://schema.org",
    "@type": "Review",
    author: { "@type": "Person", name: t.author },
    reviewBody: t.body,
    reviewRating: {
      "@type": "Rating",
      ratingValue: t.rating,
      bestRating: 5,
      worstRating: 1,
    },
    datePublished: t.date,
    itemReviewed: {
      "@type": "LodgingBusiness",
      name: "Son's Guadalupe",
      url: "https://sonsguadalupe.com",
    },
  }));

  return (
    <>
      <Helmet>
        {reviewSchemas.map((s, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(s)}
          </script>
        ))}
      </Helmet>

      <section className="py-16 md:py-20 bg-cream">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center gap-1 mb-3">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star
                  key={i}
                  className="w-5 h-5 text-yellow-400 fill-yellow-400"
                />
              ))}
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-teal mb-3">
              {heading}
            </h2>
            <p className="text-charcoal/70 text-lg">{subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((t) => (
              <article
                key={t.author + t.date}
                className="bg-white rounded-2xl shadow-soft p-6 flex flex-col"
              >
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 text-yellow-400 fill-yellow-400"
                    />
                  ))}
                </div>
                {t.highlight && (
                  <p className="font-heading font-bold text-charcoal text-lg leading-snug mb-3">
                    "{t.highlight}"
                  </p>
                )}
                <p className="text-charcoal/70 text-sm leading-relaxed mb-4 flex-1">
                  {t.body}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-charcoal/10">
                  <span className="font-heading font-bold text-charcoal text-sm">
                    {t.author}
                  </span>
                  <span className="text-xs text-charcoal/50">via Google</span>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <a
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-teal font-medium hover:underline"
            >
              See all reviews on Google →
            </a>
            <span className="hidden sm:inline text-charcoal/30">•</span>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => gtagReportConversion("review_click")}
              className="inline-flex items-center gap-2 bg-teal text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-teal/90 transition-colors"
            >
              <Star className="w-4 h-4 fill-white" />
              Leave a Google review
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
